import { JSX, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { getReadingContent } from '../../dto/reading';
import { useLayout } from '../../hooks/layout.hook';
import { useReading } from '../../hooks/reading.hook';
import { useScroll } from '../../hooks/scroll.hook';

export default function ReadingScreen(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { dailyReading, markReadingCompleted, hasReadingToDo } = useReading();
  const { scrollsBottom } = useScroll();

  const [isFirstRender, setIsFirstRender] = useState(true);

  const content = useMemo(
    () => dailyReading && getReadingContent(dailyReading, i18n.language),
    [dailyReading, i18n.language],
  );

  useLayout(
    () => ({
      activeMenu: 'readings',
      title: content?.title || t('Daily reading'),
      loading: !content,
    }),
    [t, content],
  );

  useEffect(() => {
    // wait for scroll activation
    if (isFirstRender) {
      setIsFirstRender(false);
      return;
    }

    if (!scrollsBottom && hasReadingToDo && dailyReading) markReadingCompleted(dailyReading.id);
  }, [scrollsBottom, hasReadingToDo, markReadingCompleted, isFirstRender, dailyReading]);

  return content ? (
    <div className="space-y-2 font-serif text-lg leading-snug">
      <ReactMarkdown
        components={{
          h1: ({ ...props }) => <h1 className="text-2xl font-bold mt-6 mb-3 first:mt-0 font-sans" {...props} />,
          h2: ({ ...props }) => <h2 className="text-xl font-bold mt-5 mb-2 first:mt-0 font-sans" {...props} />,
          h3: ({ ...props }) => <h3 className="text-lg font-bold mt-4 mb-2 first:mt-0 font-sans" {...props} />,
          blockquote: ({ ...props }) => (
            <blockquote
              className="border-l-4 border-primary pl-4 my-4 text-base-content/80 font-sans text-base"
              {...props}
            />
          ),
          p: ({ ...props }) => <p className="text-base-content/80 hyphens-auto text-justify" {...props} />,
          ul: ({ ...props }) => <ul className="list-disc ml-7 text-base-content/80 space-y-1" {...props} />,
          ol: ({ ...props }) => <ol className="list-decimal ml-7 text-base-content/80 space-y-1" {...props} />,
          hr: ({ ...props }) => <div className="divider my-2" {...props} />,
          strong: ({ ...props }) => <strong className="font-bold" {...props} />,
          em: ({ ...props }) => <em className="italic" {...props} />,
          a: ({ ...props }) => <a className="link link-accent" {...props} />,
        }}
      >
        {content.text}
      </ReactMarkdown>
    </div>
  ) : (
    <></>
  );
}
